/* olmia-witvlak.js — laat een witte fotoachtergrond opgaan in het kader.
 *
 * HET PROBLEEM (gemeten 2026-08-31 op /products/universal-robots-ur15)
 *   templates.css r288 geeft `.gallery__main` een radiaal verloop van #fff in het
 *   midden naar --mist aan de randen. Een uitgeknipte foto met transparante
 *   achtergrond staat daar mooi in; dat is waar het ontwerp van uitging.
 *   Olmia's fabrieksfoto's hebben echter een DICHTE witte achtergrond. Die valt in
 *   het midden weg en krijgt een harde rand waar het verloop donkerder wordt: een
 *   zichtbaar wit blok in een getint kader.
 *
 * WAAROM NIET GEWOON OVERAL mix-blend-mode: multiply
 *   Omdat het dan de donkere foto's sloopt. Van de vijf beelden bij de UR15 hebben er
 *   drie witte hoeken, één transparante hoeken en één geen van beide. Blenden hoort
 *   alleen bij die eerste groep.
 *
 * HOE
 *   Van elke foto worden vier hoekpixels bemonsterd. Zijn ze alle vier bijna wit én
 *   ondoorzichtig, dan krijgt de foto de klasse `is-witvlak` en laat de CSS het wit
 *   wegvallen. Mislukt het bemonsteren (CORS, een trage verbinding, een oud beeld),
 *   dan gebeurt er NIETS en ziet de bezoeker precies wat hij nu ook ziet. Fail-safe:
 *   een mislukte meting mag nooit een foto veranderen.
 */
(function () {
  'use strict';
  if (!('createImageBitmap' in window) || !('OffscreenCanvas' in window)) return;

  /* Op SRC bijhouden, niet op het element. De galerij wisselt van beeld door de `src`
     van hetzelfde <img> te vervangen. Met een WeakSet op het element werd zo'n wissel
     als "al behandeld" gezien en bleef de klasse achterwege: precies de foto's die de
     behandeling nodig hadden, kregen hem niet. Gemeten 2026-08-31: na een klik op een
     witte thumbnail stond op de hoofdfoto is-witvlak=false. */
  const CACHE = new Map();   // src -> boolean, zodat dezelfde foto niet twee keer wordt gehaald

  async function isWitVlak(src) {
    if (CACHE.has(src)) return CACHE.get(src);
    try {
      const blob = await fetch(src, { mode: 'cors' }).then((r) => (r.ok ? r.blob() : null));
      if (!blob) { CACHE.set(src, false); return false; }
      const bmp = await createImageBitmap(blob);
      const c = new OffscreenCanvas(9, 9);
      const x = c.getContext('2d', { willReadFrequently: true });
      x.drawImage(bmp, 0, 0, 9, 9);
      const d = x.getImageData(0, 0, 9, 9).data;
      /* vier hoeken van het 9x9-raster */
      const hoeken = [0, 8, 72, 80].map((i) => [d[i * 4], d[i * 4 + 1], d[i * 4 + 2], d[i * 4 + 3]]);
      const wit = hoeken.every((p) => p[3] > 250 && p[0] > 244 && p[1] > 244 && p[2] > 244);
      CACHE.set(src, wit);
      return wit;
    } catch (e) {
      CACHE.set(src, false);
      return false;
    }
  }

  async function behandel(img) {
    if (!img) return;
    const src = img.currentSrc || img.src;
    if (!src) return;
    if (img.dataset.witvlakVoor === src) return;   // deze exacte bron is al beoordeeld
    img.dataset.witvlakVoor = src;
    const wit = await isWitVlak(src);
    /* Altijd zetten EN weghalen: wisselt de galerij van een witte naar een donkere
       foto, dan moet de behandeling ook weer verdwijnen. */
    img.classList.toggle('is-witvlak', wit);
  }

  function scan(scope) {
    (scope || document).querySelectorAll('.gallery__main img, .gallery__thumbs img').forEach((i) => {
      if (i.complete) behandel(i);
      /* niet `once`: hetzelfde element vuurt opnieuw bij elke wissel van bron */
      i.addEventListener('load', () => behandel(i));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();

  /* De galerij wisselt van beeld zonder herladen, en de theme-editor herbouwt secties. */
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  const g = document.querySelector('.gallery');
  if (g && 'MutationObserver' in window) {
    new MutationObserver(() => scan(g)).observe(g, { subtree: true, attributes: true, attributeFilter: ['src', 'srcset'] });
  }
})();
